import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';

import { Observable, of, startWith, Subject, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import { finShare } from '@fcom/rx';
import { pathIsWithinHardcodedPath } from '@fcom/core/utils/app-url-utils';
import { RootPaths } from '@fcom/core/constants';

import { QualtricsSurvey } from '../../qualtrics/qualtrics.utils';
import { FeedbackService } from '../../services/feedback/feedback.service';
import { MediaQueryService } from '../../services';

@Component({
  selector: 'fin-feedback-widget',
  templateUrl: './feedback-widget.component.html',
  styleUrls: ['./feedback-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FeedbackWidgetComponent implements OnInit, OnDestroy {
  private feedbackService = inject(FeedbackService);
  private router = inject(Router);
  private mediaQueryService = inject(MediaQueryService);

  removeGlobalListener?: () => void;
  openSurveyTrigger$: Subject<boolean> = new Subject();
  visibility$: Observable<boolean>;
  feedbackSurvey$: Observable<QualtricsSurvey>;

  ngOnInit(): void {
    const navigationEvent$ = this.router.events.pipe(
      filter((event: Event) => event instanceof NavigationEnd),
      startWith({ url: this.router.url } as NavigationEnd)
    );
    const canShowInViewport$ = this.mediaQueryService.isBreakpoint$('tablet_down').pipe(map((value) => !value));

    const canShowFeedbackButton$ = navigationEvent$.pipe(
      switchMap((event: NavigationEnd) => {
        const isVoluntaryChangeRoute = event.url.includes(RootPaths.VOLUNTARY_CHANGE_ROOT);

        if (
          (pathIsWithinHardcodedPath(event.url, RootPaths.MANAGE_BOOKING_ROOT) && !isVoluntaryChangeRoute) ||
          pathIsWithinHardcodedPath(event.url, RootPaths.CHECK_IN_ROOT)
        ) {
          return of(true);
        }
        return canShowInViewport$;
      })
    );

    const serviceVisibility$ = this.feedbackService.visibility$.pipe(distinctUntilChanged(), finShare());
    this.visibility$ = canShowFeedbackButton$.pipe(switchMap((canShow) => (canShow ? serviceVisibility$ : of(false))));
    this.feedbackSurvey$ = this.feedbackService.feedbackSurvey$.pipe(distinctUntilChanged());
  }

  ngOnDestroy(): void {
    if (this.removeGlobalListener) {
      this.removeGlobalListener();
    }
  }

  openModal(event: MouseEvent): void {
    event.stopPropagation();

    this.openSurveyTrigger$.next(true);
  }
}
